import cloneDeep from 'lodash.clonedeep';
import memoize from 'lodash.memoize';
import { Translator } from '../Translator';
import { Filter } from 'model/Filters';
import { Aggregations } from 'model/SearchResults';
import durationSubOptions from './subOptions/durationSubOptions';
import priceSubOptions from './subOptions/priceSubOptions';
import cuisineSubOptions from './subOptions/cusineSubOptions';
import languagesSubOptions from './subOptions/languagesSubOptions';
import transportationModeSubOptions from './subOptions/transportationModeSubOptions';
import availabilitySubOptions from './subOptions/availabilitySubOptions';
import activityTypeSubOptions from './subOptions/activityTypeSubOptions';
import subCategoriesSubOptions from './subOptions/subCategoriesSubOptions';
import tagSubOptions from './subOptions/tagSubOptions';

const filters = [
  {
    id: 991,
    order: 0,
    uid: 'activityType.activity',
    name: Translator.translate('UserEnvironment.Filters.categories', {}, true, 'Categories'),
    queryName: 'category[]',
    subOptions: activityTypeSubOptions,
  },
  {
    id: 1,
    order: 1,
    uid: 'subcategory.items[]',
    name: Translator.translate('UserEnvironment.Filters.subcategories', {}, true, 'Sub-categories'),
    queryName: 'subcategory[]',
    subOptions: subCategoriesSubOptions,
  },
  {
    id: 7,
    order: 2,
    uid: 'tags.items[]',
    name: Translator.translate('SearchPage.TagsFilter.label', {}, true, 'Tags'),
    queryName: 'tag[]',
    collapsible: true,
    subOptions: tagSubOptions,
  },
  {
    id: 3,
    order: 3,
    uid: 'duration.items[]',
    name: Translator.translate('UserEnvironment.Filters.duration', {}, true, 'Duration'),
    queryName: 'duration[]',
    subOptions: durationSubOptions,
  },
  {
    id: 2,
    order: 3,
    uid: 'price.items[]',
    name: Translator.translate('UserEnvironment.Filters.priceRange', {}, true, 'Price range'),
    queryName: 'price[]',
    subOptions: priceSubOptions,
  },
  {
    id: 4,
    uid: 'instantconfirmation',
    queryName: 'date.instantconfirmation',
    name: Translator.translate('UserEnvironment.Filters.instantlyConfirmed', {}, true, 'Instantly Confirmed'),
    value: 'true',
    subOptions: [],
  },
  {
    id: 6,
    uid: 'family-friendly',
    queryName: 'family-friendly',
    name: Translator.translate('FamilyFriendly.label', {}, true, 'Family Friendly'),
    value: 'family-friendly',
    subOptions: [],
  },
  {
    id: 8,
    order: 4,
    uid: 'cuisines.items',
    name: Translator.translate('SearchPage.CuisinesFilter.label', {}, true, 'Cuisines'),
    queryName: 'cuisine[]',
    collapsible: true,
    subOptions: cuisineSubOptions,
  },
  {
    id: 9,
    order: 5,
    uid: 'languages.items[]',
    name: Translator.translate('UserEnvironment.Filters.language', {}, true, 'Language'),
    queryName: 'language[]',
    subOptions: languagesSubOptions,
  },
  {
    id: 10,
    uid: 'keywords',
    name: Translator.translate('UserEnvironment.Filters.keywords', {}, true, 'Keywords'),
    queryName: 'keywords[]',
    searchWithOthersQueryName: 'query',
    value: undefined,
    subOptions: [],
  },
  {
    id: 11,
    uid: 'adults',
    name: Translator.translate('UserEnvironment.Filters.adults', {}, true, 'Adults'),
    queryName: 'adults',
    value: undefined,
    subOptions: [],
    defaultValue: '4',
  },
  {
    id: 12,
    uid: 'children',
    name: Translator.translate('UserEnvironment.Filters.children', {}, true, 'Children'),
    queryName: 'children',
    value: undefined,
    subOptions: [],
  },
  {
    id: 13,
    uid: 'date.from',
    name: Translator.translate('UserEnvironment.Filters.dateFrom', {}, true, 'From'),
    queryName: 'date.from',
    value: null,
    subOptions: [],
  },
  {
    id: 14,
    uid: 'date.to',
    name: Translator.translate('UserEnvironment.Filters.dateTo', {}, true, 'To'),
    queryName: 'date.to',
    value: null,
    subOptions: [],
  },
  {
    id: 15,
    order: 6,
    uid: 'other.transport_mode.items[]',
    name: Translator.translate('UserEnvironment.Filters.transportMode', {}, true, 'Transport mode'),
    queryName: 'transportmode[]',
    hideIfEmpty: true,
    subOptions: transportationModeSubOptions,
  },
  {
    id: 16,
    uid: 'other.availability.items[]',
    name: Translator.translate('UserEnvironment.Filters.availability', {}, true, 'Availability'),
    queryName: 'availability[]',
    subOptions: availabilitySubOptions,
  },
] as Filter[];

export default filters;
export const getFilter = (uid): Filter => {
  return cloneDeep(filters.find(filter => filter.uid === uid));
};

export function createFilter(uid) {
  const filter = getFilter(uid);
  filter.subOptions = [];
  filter.queryName = filter.queryName || undefined;
  return filter;
}

export function getSubOption(uid, option) {
  return getFilter(uid).subOptions.find(({ value }) => option === value);
}

export const mapSubcategoryToTours = {
  'food-tours': 'tours',
  'city-tours': 'tours',
  'off-the-beaten-track': 'tours',
  'night-tours': 'tours',
  'skip-the-line-tours': 'tours',
  'day-trips': 'tours',
  'multi-day-trips': 'tours',
  'photo-tours': 'tours',
  'shopping-tours': 'tours',
  'art-culture-tours': 'tours',
  'wine-beer-tours': 'tours',
};

export const mapSubcategoryToActivities = {
  'home-dinners': 'activities',
  'cooking-classes': 'activities',
  'wildlife-nature': 'activities',
  'sports-leisure': 'activities',
  'food-drink-tastings': 'activities',
  tastings: 'activities',
  workshops: 'activities',
};

export const mapSubcategoryToCategory = {
  ...mapSubcategoryToTours,
  ...mapSubcategoryToActivities,
};

const getSubcategoriesFilters = (buckets, subcategoryMap) =>
  Object.keys(subcategoryMap)
    .map(key => getSubOption('subcategory.items[]', key))
    .filter(subCategory => buckets.some(({ key }) => key === subCategory.queryValue || key === subCategory.value));

const getTransportModeSubOptions = buckets =>
  transportationModeSubOptions
    .filter(({ value, queryValue }) => buckets.some(bucket => bucket.key === value || bucket.key === queryValue))
    .map(({ value }) => getSubOption('other.transport_mode.items[]', value));

export const getMoreFilters = memoize((aggregations: Aggregations) => {
  if (!aggregations) {
    return null;
  }

  return {
    priceSubOptions,
    durationSubOptions,
    toursFilter: getSubOption('activityType.activity', 'tours'),
    toursSubcategoriesFilters: getSubcategoriesFilters(aggregations.global.location_filtered.subcategories.buckets, mapSubcategoryToTours),
    activitiesFilter: getSubOption('activityType.activity', 'activities'),
    activitiesSubcategoriesFilters: getSubcategoriesFilters(aggregations.global.location_filtered.subcategories.buckets, mapSubcategoryToActivities),
    transportMode: createFilter('other.transport_mode.items[]'),
    transportModeSubOptions: getTransportModeSubOptions(aggregations.global.location_filtered.transport_mode.buckets),
    priceFilter: createFilter('price.items[]'),
    durationFilter: createFilter('duration.items[]'),
    familyFriendlyFilter: createFilter('family-friendly'),
    instantConfirmationFilter: createFilter('instantconfirmation'),
  };
}, (aggregations: Aggregations) => aggregations ? JSON.stringify(aggregations.global.location_filtered) : 'null');
